<template>
  <v-container>
    <div class="text-center">
      <SectionTitle class="d-inline-block">Simule seus ganhos</SectionTitle>
      <p class="section-subtitle mt-4">
        Informe a sua carteira e as vendas do mês para ver quanto você recebe.
      </p>
    </div>

    <div class="simulador mt-8">
      <div class="simulador__entradas">
        <div v-for="grupo in grupos" :key="grupo.id" class="grupo">
          <div class="grupo__top">
            <h3 class="grupo__titulo">{{ grupo.titulo }}</h3>
            <span class="grupo__total">{{ contagem(totais[grupo.id], grupo.unidade) }}</span>
          </div>
          <p class="grupo__desc">{{ grupo.desc }}</p>

          <div v-for="plano in planos" :key="plano.id" class="plano">
            <div class="plano__top">
              <span class="plano__nome">
                {{ plano.nome }} <span class="plano__preco">· {{ moeda(plano.preco) }}/mês</span>
              </span>
              <strong class="plano__qtd">{{ quantidades[grupo.id][plano.id] }}</strong>
            </div>

            <v-slider
              v-model="quantidades[grupo.id][plano.id]"
              :min="0"
              :max="grupo.max"
              :aria-label="`${plano.nome} ${grupo.aria}`"
              color="#23cf65"
              track-color="rgba(35, 207, 101, 0.25)"
              hide-details
              @change="registrarUso"
            >
              <template #prepend>
                <v-btn icon small :aria-label="`Menos um ${plano.nome} ${grupo.aria}`" @click="ajustar(grupo, plano.id, -1)">
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
              </template>
              <template #append>
                <v-btn icon small :aria-label="`Mais um ${plano.nome} ${grupo.aria}`" @click="ajustar(grupo, plano.id, 1)">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
            </v-slider>
          </div>
        </div>
      </div>

      <div class="simulador__resultado" aria-live="polite">
        <span class="resultado__eyebrow">Seus ganhos no mês</span>
        <div class="resultado__valor">
          {{ moeda(ganhoMensal) }}<span class="resultado__periodo">/mês</span>
        </div>
        <div class="resultado__ano">{{ moeda(ganhoMensal * 12) }} por ano</div>

        <div class="composicao">
          <div class="composicao__item">
            <div>
              <span class="composicao__rotulo">Comissão recorrente</span>
              <span class="composicao__conta">{{ contaRecorrente }}</span>
            </div>
            <strong>{{ moeda(comissaoRecorrente) }}</strong>
          </div>
          <div class="composicao__item">
            <div>
              <span class="composicao__rotulo">Comissão na venda</span>
              <span class="composicao__conta">{{ contaVenda }}</span>
            </div>
            <strong>{{ moeda(comissaoVenda) }}</strong>
          </div>
        </div>

        <div class="faixas">
          <div
            v-for="(item, i) in faixas"
            :key="i"
            class="faixas__item"
            :class="{ 'faixas__item--ativa': item === faixa }"
          >
            <strong>{{ item.percentual }}%</strong>
            <span>{{ rotuloFaixa(item) }}</span>
          </div>
        </div>

        <p class="resultado__proxima">
          <template v-if="proximaFaixa">
            Com mais {{ contagem(faltam, ['cliente', 'clientes']) }} na carteira,
            você chega à faixa de {{ proximaFaixa.percentual }}%.
          </template>
          <template v-else>Você está na maior faixa de comissão.</template>
        </p>

        <CtaButton>Começar a revender</CtaButton>
      </div>
    </div>

    <p class="nota">
      A comissão na venda considera o máximo de {{ percentualVenda }}% da primeira mensalidade.
    </p>
  </v-container>
</template>

<script>
  import SectionTitle from '@/components/SectionTitle.vue';
  import CtaButton from '@/components/CtaButton.vue';
  import { trackClick } from '@/utils/lpTracker';
  import {
    FAIXAS_RECORRENTE,
    PERCENTUAL_PRIMEIRA_MENSALIDADE,
    faixaDaCarteira,
    rotuloFaixa,
  } from '@/utils/comissaoRevenda';

  const formatoMoeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

  export default {
    name: 'RevendaSimuladorSection',

    components: {
      SectionTitle,
      CtaButton,
    },

    data: () => ({
      planos: [
        { id: 'essencial', nome: 'Essencial', preco: 197 },
        { id: 'profissional', nome: 'Profissional', preco: 259 },
      ],
      // A carteira define a faixa e gera a recorrente; as vendas novas geram a comissão na venda.
      grupos: [
        {
          id: 'carteira',
          titulo: 'Sua carteira',
          desc: 'Clientes ativos, que geram a comissão recorrente.',
          aria: 'na carteira',
          unidade: ['cliente', 'clientes'],
          max: 100,
        },
        {
          id: 'novas',
          titulo: 'Vendas novas no mês',
          desc: 'Clientes fechados no mês, que geram a comissão na venda.',
          aria: 'em vendas novas no mês',
          unidade: ['venda', 'vendas'],
          max: 30,
        },
      ],
      quantidades: {
        carteira: { essencial: 15, profissional: 5 },
        novas: { essencial: 3, profissional: 1 },
      },
      faixas: FAIXAS_RECORRENTE,
      percentualVenda: PERCENTUAL_PRIMEIRA_MENSALIDADE,
      usado: false,
    }),

    computed: {
      totais() {
        return {
          carteira: this.somar('carteira', () => 1),
          novas: this.somar('novas', () => 1),
        };
      },

      mensalidades() {
        return this.somar('carteira', plano => plano.preco);
      },

      primeirasMensalidades() {
        return this.somar('novas', plano => plano.preco);
      },

      faixa() {
        return faixaDaCarteira(this.totais.carteira);
      },

      comissaoRecorrente() {
        return this.faixa ? this.mensalidades * this.faixa.percentual / 100 : 0;
      },

      comissaoVenda() {
        return this.primeirasMensalidades * this.percentualVenda / 100;
      },

      ganhoMensal() {
        return this.comissaoRecorrente + this.comissaoVenda;
      },

      contaRecorrente() {
        if (!this.faixa) return 'Sem clientes na carteira';
        return `${this.faixa.percentual}% de ${this.moeda(this.mensalidades)} em mensalidades`;
      },

      contaVenda() {
        if (!this.totais.novas) return 'Sem vendas novas no mês';
        return `${this.percentualVenda}% da 1ª mensalidade de ${this.contagem(this.totais.novas, ['venda', 'vendas'])}`;
      },

      proximaFaixa() {
        return FAIXAS_RECORRENTE.find(f => f.min > this.totais.carteira) || null;
      },

      faltam() {
        return this.proximaFaixa ? this.proximaFaixa.min - this.totais.carteira : 0;
      },
    },

    methods: {
      rotuloFaixa,

      moeda(valor) {
        return formatoMoeda.format(valor);
      },

      contagem(n, [singular, plural]) {
        return `${n} ${n === 1 ? singular : plural}`;
      },

      somar(grupoId, valorPorCliente) {
        return this.planos.reduce(
          (soma, plano) => soma + this.quantidades[grupoId][plano.id] * valorPorCliente(plano),
          0,
        );
      },

      ajustar(grupo, planoId, delta) {
        const valor = this.quantidades[grupo.id][planoId] + delta;
        this.quantidades[grupo.id][planoId] = Math.min(grupo.max, Math.max(0, valor));
        this.registrarUso();
      },

      // Um evento por visita basta para saber quantos usaram o simulador.
      registrarUso() {
        if (this.usado) return;
        this.usado = true;
        trackClick('Revenda - Simulador de ganhos');
      },
    },
  };
</script>

<style scoped lang="scss">
  .section-subtitle {
    font-size: 17px;
    line-height: 1.7;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: 780px) {
      font-size: 15px;
    }
  }

  .simulador {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    border-radius: 18px;
    overflow: hidden;
    background: var(--card-bg);
    border: 1px solid var(--card-border);

    @media (max-width: 960px) {
      grid-template-columns: minmax(0, 1fr);
    }

    &__entradas {
      padding: 32px;

      @media (max-width: 960px) {
        padding: 26px 22px;
      }
    }

    &__resultado {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: 32px;
      background: rgba(35, 207, 101, 0.07);
      border-left: 1px solid var(--card-border);

      @media (max-width: 960px) {
        padding: 28px 22px;
        border-left: none;
        border-top: 1px solid var(--card-border);
      }
    }
  }

  /* ── Entradas ── */
  .grupo + .grupo {
    margin-top: 28px;
    padding-top: 24px;
    border-top: 1px solid var(--card-border);
  }

  .grupo {
    &__top {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 12px;
    }

    &__titulo {
      margin: 0;
      font-size: 19px;
      font-weight: 700;
      color: var(--text-primary);
    }

    &__total {
      font-size: 14px;
      font-weight: 600;
      color: var(--text-muted);
      white-space: nowrap;
    }

    &__desc {
      margin: 4px 0 0;
      font-size: 14px;
      color: var(--text-muted);
    }
  }

  .plano {
    margin-top: 16px;

    &__top {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 12px;
    }

    &__nome {
      font-size: 15px;
      font-weight: 700;
      color: var(--text-primary);
    }

    &__preco {
      font-weight: 400;
      color: var(--text-muted);
    }

    &__qtd {
      font-size: 22px;
      font-weight: 800;
      color: var(--text-primary);
      font-variant-numeric: tabular-nums;
    }
  }

  /* ── Resultado ── */
  .resultado {
    &__eyebrow {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--text-muted);
    }

    &__valor {
      margin-top: 14px;
      font-size: 46px;
      font-weight: 800;
      line-height: 1;
      color: #23cf65;
      font-variant-numeric: tabular-nums;

      @media (max-width: 780px) {
        font-size: 36px;
      }
    }

    &__periodo {
      margin-left: 4px;
      font-size: 18px;
      font-weight: 600;
      color: var(--text-muted);
    }

    &__ano {
      margin-top: 10px;
      font-size: 16px;
      font-weight: 600;
      color: var(--text-secondary);
    }

    &__proxima {
      margin: 14px 0 0;
      min-height: 24px;
      font-size: 15px;
      color: var(--text-secondary);
    }
  }

  .composicao {
    width: 100%;
    margin-top: 22px;
    text-align: left;

    &__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 0;
      border-top: 1px solid var(--card-border);

      &:last-child {
        border-bottom: 1px solid var(--card-border);
      }

      strong {
        font-size: 17px;
        font-weight: 700;
        color: var(--text-primary);
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
    }

    &__rotulo {
      display: block;
      font-size: 15px;
      font-weight: 600;
      color: var(--text-primary);
    }

    &__conta {
      display: block;
      font-size: 13px;
      color: var(--text-muted);
    }
  }

  .faixas {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
    margin-top: 22px;

    @media (max-width: 600px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__item {
      padding: 10px 6px;
      border-radius: 12px;
      border: 1px solid var(--card-border);
      transition: background 0.2s ease, border-color 0.2s ease;

      strong {
        display: block;
        font-size: 18px;
        font-weight: 800;
        color: var(--text-primary);
      }

      span {
        display: block;
        margin-top: 2px;
        font-size: 12px;
        line-height: 1.3;
        color: var(--text-muted);
      }

      &--ativa {
        background: #23cf65;
        border-color: #23cf65;

        strong,
        span {
          color: #ffffff;
        }
      }
    }
  }

  .nota {
    margin: 14px 0 0;
    font-size: 13px;
    text-align: center;
    color: var(--text-hint);
  }
</style>
